import React from 'react';

import { useTranslations } from 'next-intl';
import styled from 'styled-components';

import { LOCALES } from '@/i18n';
import Flags from '@/ui/components/Flags';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  gap: 1.25rem;

  ${({ theme }) => theme.breakpoints.down('md')} {
    gap: 0.625rem;
  }
`;

const getFlagUrl = ({ locale, basePath, slug }) => {
  if (basePath.startsWith('/')) {
    return `/${locale}${basePath}`;
  }

  return `/${locale}/${basePath}${slug}`;
};

type FlagItem = {
  locale: string;
  link: string;
};

/**
 * Language switcher for content (Flags)
 * basePath - path to the content, e.g. 'articles/' or '/slicer-profiles' (starting with '/' - dont uses slug)
 */
export default function ContentLanguageSwitcher({
  translations,
  contentType = 'Content',
  basePath = '',
  flagsOnly = false,
}) {
  const translate = useTranslations();

  const items = React.useMemo(() => {
    const items: FlagItem[] = [];

    LOCALES.forEach(locale => {
      const translation = translations?.[locale];
      if (translation) {
        items.push({
          locale,
          link: getFlagUrl({ locale, basePath, slug: translation.slug }),
        });
      }
    });

    return items;
  }, [translations, basePath]);

  if (!translations) {
    return null;
  }

  if (flagsOnly) {
    return <Flags items={items} />;
  }

  return (
    <Wrapper>
      {items.length > 0 && (
        <>
          <div>
            {contentType} {translate('article-languages')}
          </div>
          <Flags items={items} />
        </>
      )}
    </Wrapper>
  );
}
